import { DEFAULT_QCS_LIMITS, USAGE_METRIC, getNextResetDate } from '../../../constants/usageLimits'
import { QCS_DEV_ENABLED } from '../../../constants/devFlags'

export const USAGE_STUB_KEY = 'qcsDevUsageStub'
const STATE_KEY = 'qcsDevUsageState'
const AUDITS_KEY = 'qcsDevAudits'

const AUDIT_DURATION_MS = 4000

export const isUsageStubEnabled = () => QCS_DEV_ENABLED

export const setUsageStubEnabled = enabled => {
  try {
    if (enabled) {
      window.localStorage.setItem(USAGE_STUB_KEY, 'on')
    } else {
      window.localStorage.setItem(USAGE_STUB_KEY, 'off')
    }
  } catch {
    // Development affordance only.
  }
}

const read = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

const write = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Quota or private browsing.
  }
}

const seeded = (botId, salt) => {
  const value = Math.sin(Number(botId) * 12.9898 + salt * 78.233) * 43758.5453
  return value - Math.floor(value)
}

const seedMetric = (botId, metric, salt) => {
  const limit = DEFAULT_QCS_LIMITS[metric]
  const roll = seeded(botId, salt)

  let used
  if (roll < 0.2) {
    used = limit
  } else if (roll < 0.45) {
    used = Math.max(Math.ceil(limit * 0.85), limit - 1)
  } else {
    used = Math.floor(roll * limit * 0.6)
  }

  return { used, limit }
}

const seedBot = botId => ({
  botId: Number(botId),
  periodStart: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
  periodEnd: getNextResetDate().toISOString(),
  metrics: {
    [USAGE_METRIC.CARE_PLAN_AUDITS]: seedMetric(botId, USAGE_METRIC.CARE_PLAN_AUDITS, 1),
    [USAGE_METRIC.SUPERVISION_SESSIONS]: seedMetric(botId, USAGE_METRIC.SUPERVISION_SESSIONS, 2),
  },
})

const getState = () => read(STATE_KEY, {})

const getBotState = botId => {
  const state = getState()
  const key = String(botId)
  if (!state[key]) {
    state[key] = seedBot(botId)
    write(STATE_KEY, state)
  }
  return state[key]
}

const setBotState = (botId, next) => {
  const state = getState()
  state[String(botId)] = next
  write(STATE_KEY, state)
  return next
}

export const stubUsageFor = botId => getBotState(botId)

export const stubBulkUsageFor = botIds =>
  (botIds || []).reduce((acc, botId) => {
    acc[botId] = getBotState(botId)
    return acc
  }, {})

export const stubSetLimits = (botId, limits) => {
  const current = getBotState(botId)
  const metrics = { ...current.metrics }

  Object.entries(limits || {}).forEach(([metric, limit]) => {
    metrics[metric] = { ...(metrics[metric] || { used: 0 }), limit }
  })

  return setBotState(botId, { ...current, metrics })
}

const getAudits = () => read(AUDITS_KEY, {})

export const stubStartAudit = (botId, { patientId, patientName, carePlans, organisation }) => {
  const current = getBotState(botId)
  const entry = current.metrics[USAGE_METRIC.CARE_PLAN_AUDITS] || { used: 0, limit: 0 }
  const unlimited = entry.limit === null || entry.limit === undefined

  if (!unlimited && entry.used >= entry.limit) {
    return { ok: false, quotaExceeded: true }
  }

  setBotState(botId, {
    ...current,
    metrics: {
      ...current.metrics,
      [USAGE_METRIC.CARE_PLAN_AUDITS]: { ...entry, used: entry.used + 1 },
    },
  })

  const id = `dev-audit-${botId}-${patientId}-${getAudits().__seq || 0}-${patientName?.length || 0}`
  const audits = getAudits()
  audits.__seq = (audits.__seq || 0) + 1
  audits[id] = {
    id,
    patientId,
    patientName,
    organisation,
    carePlans: carePlans || [],
    status: 'PROCESSING',
    createdAt: new Date().toISOString(),
    readyAt: Date.now() + AUDIT_DURATION_MS,
  }
  write(AUDITS_KEY, audits)

  return { ok: true, audit: { id, patientId, status: 'PROCESSING', createdAt: audits[id].createdAt } }
}

export const stubGetAudit = auditId => {
  const audits = getAudits()
  const audit = audits[auditId]
  if (!audit) return null

  if (audit.status === 'PROCESSING' && Date.now() >= audit.readyAt) {
    audit.status = 'READY'
    write(AUDITS_KEY, audits)
  }

  return audit
}

export const stubAuditRecord = auditId => getAudits()[auditId] || null

/**
 * Every audit recorded for one service user, newest first — the version history
 * the result screen switches between. Ripe PROCESSING entries are flipped to
 * READY on read, same as `stubGetAudit`.
 */
export const stubListAudits = patientId => {
  const audits = getAudits()
  let changed = false

  const list = Object.keys(audits)
    .filter(key => key !== '__seq')
    .map(key => audits[key])
    .filter(audit => audit && Number(audit.patientId) === Number(patientId))
    .map(audit => {
      if (audit.status === 'PROCESSING' && Date.now() >= audit.readyAt) {
        audit.status = 'READY'
        changed = true
      }
      return audit
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  if (changed) write(AUDITS_KEY, audits)
  return list
}
